import { dayjs, getFilledArrayOrDefault } from "../../../../../../utils";

export const normalizeReportChildrenLevel = (childrenLevel: unknown): 1 | 2 => (
    Number(childrenLevel) === 1 ? 1 : 2
);

export const getReportHierarchyRequestParams = (configuredLevel: unknown) => {
    const childrenLevel = normalizeReportChildrenLevel(configuredLevel);
    return {
        childrenLevel,
        returnGrandChildren: childrenLevel === 2,
    };
};

/**
 * Build the `check_location` query string for the section checks API call.
 *
 * Includes both preferred check types AND the report-level fallback check types so the
 * EFQ-4115 fallback can surface pallets that only have a fallback (e.g. intake) check.
 */
export const buildSectionCheckLocation = (
    preferredCheckTypes: string[] | undefined | null,
    fallbackCheckTypes: string[] | undefined | null,
): string => {
    const preferred = getFilledArrayOrDefault(preferredCheckTypes);
    const fallback = getFilledArrayOrDefault(fallbackCheckTypes);
    return Array.from(new Set([...preferred, ...fallback])).join(",");
};

/**
 * Pick the latest check belonging to the given layer that matches the preferred
 * check types. If no preferred types are configured, any check counts.
 *
 * Used so that fallback checks fetched alongside the preferred checks (EFQ-4115)
 * do not masquerade as the section's main check for the parent layer.
 */
export const pickLatestPreferredCheck = (
    checks: any[] | undefined | null,
    layerId: string,
    preferredCheckTypes: string[] | undefined | null,
): any | null => {
    const preferredSet = new Set(getFilledArrayOrDefault(preferredCheckTypes));
    const matching = (checks ?? []).filter(
        (c) => c.layer_id === layerId && (preferredSet.size === 0 || preferredSet.has(c.location))
    );
    if (!matching.length) return null;
    return matching.slice().sort((a, b) => dayjs(b.created).diff(dayjs(a.created)))[0] ?? null;
};

/**
 * The calendar day a check belongs to. Uses the check's own `date` field when present,
 * otherwise derives it from `created`.
 */
export const checkDayKey = (check: any): string => check?.date || (check?.created ? dayjs(check.created).format("YYYY-MM-DD") : "");

/**
 * Keep only the single most-recent check per layer (by `created`). Used by the
 * `latest_only` merge strategy so a layer's page reflects just its latest check.
 */
export const pickLatestPerLayer = (checks: any[] | undefined | null): any[] => {
    const latestByLayer = new Map<string, any>();
    getFilledArrayOrDefault(checks).forEach((check) => {
        const existing = latestByLayer.get(check.layer_id);
        if (!existing || dayjs(check.created).diff(dayjs(existing.created)) > 0) {
            latestByLayer.set(check.layer_id, check);
        }
    });
    return Array.from(latestByLayer.values());
};

/**
 * Group checks by calendar day, returned oldest day first. Used by the
 * `merge_per_day` merge strategy which renders one set of pages per day.
 */
export const groupChecksByDay = (checks: any[] | undefined | null): Array<[string, any[]]> => {
    const byDay = new Map<string, any[]>();
    getFilledArrayOrDefault(checks).forEach((check) => {
        const key = checkDayKey(check);
        byDay.set(key, [...(byDay.get(key) || []), check]);
    });
    return Array.from(byDay.entries()).sort(([a], [b]) => a.localeCompare(b));
};

/**
 * Direct children of `layer` from a flat list of (grand)children. After `format2Frontend`
 * each child carries `${parentType}_id` keys (e.g. `order_id`, `container_id`) derived from
 * its `parents`, so we match on the parent layer's own type. Falls back to all children
 * when no parent linkage is available.
 */
export const childrenOfLayer = (allChildren: any[] | undefined | null, layer: any): any[] => {
    const children = getFilledArrayOrDefault(allChildren);
    if (!layer?.type || !layer?.id) return children;
    const matched = children.filter((child) => child?.[`${layer.type}_id`] === layer.id);
    return matched.length ? matched : children;
};

/**
 * Per layer, return the checks that should drive its page: the preferred-location checks
 * if the layer has any, otherwise its fallback-location checks (mirrors the selection in
 * `setPDFDataForIntakeReport`). When no preferred/fallback types are configured, all of the
 * layer's checks are returned. Used to pick the right granularity before grouping by day /
 * per check.
 */
export const selectPreferredOrFallbackChecks = (
    checks: any[] | undefined | null,
    preferred: string[] | undefined | null,
    fallback: string[] | undefined | null,
): any[] => {
    const preferredSet = new Set(getFilledArrayOrDefault(preferred));
    const fallbackSet = new Set(getFilledArrayOrDefault(fallback));
    const byLayer = new Map<string, any[]>();
    getFilledArrayOrDefault(checks).forEach((check) => {
        byLayer.set(check.layer_id, [...(byLayer.get(check.layer_id) || []), check]);
    });
    const result: any[] = [];
    byLayer.forEach((layerChecks) => {
        const preferredChecks = layerChecks.filter((c) => preferredSet.has(c.location));
        if (preferredChecks.length) {
            result.push(...preferredChecks);
            return;
        }
        const fallbackChecks = layerChecks.filter((c) => fallbackSet.has(c.location));
        if (fallbackChecks.length) {
            result.push(...fallbackChecks);
            return;
        }
        if (preferredSet.size === 0 && fallbackSet.size === 0) {
            result.push(...layerChecks);
        }
    });
    return result;
};

/**
 * Select checks for a parent-per-fruit-type page after the section's check types
 * have already been applied. Parent checks take precedence; child checks are the
 * valid source for reports where checks exist only on child layers.
 */
export const selectParentPerFruitTypeChecks = (
    selectedChecks: any[] | undefined | null,
    fruitTypeLayers: any[] | undefined | null,
    parentLayerId: string,
): { fruitTypeChecks: any[]; parentChecks: any[]; unitChecks: any[] } => {
    const fruitTypeLayerIds = new Set(getFilledArrayOrDefault(fruitTypeLayers).map((layer) => layer.id));
    const fruitTypeChecks = getFilledArrayOrDefault(selectedChecks).filter((check) => fruitTypeLayerIds.has(check.layer_id));
    const parentChecks = fruitTypeChecks.filter((check) => check.layer_id === parentLayerId);

    return {
        fruitTypeChecks,
        parentChecks,
        unitChecks: parentChecks.length > 0 ? parentChecks : fruitTypeChecks,
    };
};

/**
 * Flatten a check's meta array into key-value pairs on the check object itself.
 * Mirrors the pattern of format2Frontend for consistent read paths.
 */
export const flattenCheckMeta = (meta: any): Record<string, any> => {
    if (!Array.isArray(meta)) return meta ?? {};
    return meta.reduce((acc, { key, value }) => ({ ...acc, [key]: value }), {});
};

/**
 * Merge multiple checks from the same layer into a single check with combined data.
 * Aggregates avocados, images, defects while preserving individual location markers.
 */
export const mergeChecksForReport = (layerChecks: any[]): any => {
    const merged = getFilledArrayOrDefault(layerChecks).reduce(
        (acc, { avocados, defects, defect_list, images, all_images, ...i }) => ({
            ...acc,
            ...i,
            [`inspected_by_${i.location}`]: i.inspected_by,
            avocados: [...acc.avocados, ...getFilledArrayOrDefault(avocados)],
            images: [...acc.images, ...getFilledArrayOrDefault(images)],
            all_images: [...acc.all_images, ...getFilledArrayOrDefault(all_images)],
            defects: { ...acc.defects, ...defects },
            defect_list: [...acc.defect_list, ...getFilledArrayOrDefault(defect_list)],
        }),
        {
            avocados: [],
            images: [],
            all_images: [],
            defects: {},
            defect_list: []
        }
    );
    return { ...merged, ...flattenCheckMeta(merged.meta) };
};

/**
 * Get children that have any of the given layer IDs (i.e., children that belong to
 * the specified parent layers or checks).
 */
export const childrenWithLayers = (
    allChildren: any[] | undefined | null,
    layerIds: string[]
): any[] => {
    const layerIdSet = new Set(layerIds);
    return getFilledArrayOrDefault(allChildren).filter((child) => layerIdSet.has(child.id));
};

/**
 * True when a fruit type should be rendered as its own section.
 * Excludes placeholder / aggregate values like "mixed".
 */
export const isRenderableFruitType = (fruitType: string | undefined | null): boolean => {
    if (!fruitType) return false;
    return fruitType !== "mixed";
};

/**
 * Derive distinct, renderable fruit types from a set of layers.
 */
export const deriveFruitTypesFromLayers = (layers: any[] | undefined | null): string[] => {
    return Array.from(
        new Set(
            getFilledArrayOrDefault(layers)
                .map((layer) => layer?.fruit_type)
                .filter((fruitType): fruitType is string => isRenderableFruitType(fruitType))
        )
    );
};
